import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, PackageSearch, Star } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/Store";
import { apiError, eur, http, optionsLabel, ZONES } from "@/lib/api";
import { Container, useSeo } from "@/components/site/common";
import { ReviewDialog } from "@/components/site/ReviewForm";

const STEPS = [["novo", "Recebido"], ["em_producao", "Em produção"], ["enviado", "Enviado"], ["entregue", "Entregue"]];
const TYPE = { online: "Pago online", whatsapp: "Pedido por WhatsApp", custom: "Ideia personalizada" };

function Timeline({ status }) {
  if (status === "cancelado") return <p className="mt-6 rounded-xl bg-red-50 p-4 text-sm font-semibold text-red-700" data-testid="track-cancelled">Este pedido foi cancelado.</p>;
  const idx = STEPS.findIndex(([k]) => k === status);
  return (
    <ol className="mt-8 grid grid-cols-4 gap-2" data-testid="track-timeline">
      {STEPS.map(([k, label], i) => (
        <li key={k} className="flex flex-col items-center text-center" data-testid={`track-step-${k}`} data-done={i <= idx}>
          <span className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-colors ${i <= idx ? "brand-gradient text-white" : "bg-[var(--lilac)] text-[var(--muted-ink)]"}`}>{i < idx ? <Check className="h-4 w-4" /> : i + 1}</span>
          <span className={`mt-2 text-xs sm:text-sm ${i === idx ? "font-bold" : "text-[var(--muted-ink)]"}`}>{label}</span>
        </li>
      ))}
    </ol>
  );
}

export default function TrackPage() {
  const [params] = useSearchParams();
  const { products } = useStore();
  const [f, setF] = useState({ number: params.get("n") || "", phone: "" });
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  useSeo("Acompanhar pedido", "Consulta o estado do teu pedido DUSA com o número do pedido e o telefone.");
  const canReview = ["enviado", "entregue"].includes(order?.status);

  const search = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try { const { data } = await http.post("/orders/track", f); setOrder(data); }
    catch (err) { setOrder(null); toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <Container className="max-w-3xl py-16">
      <div data-testid="track-page">
        <p className="eyebrow">Acompanhar pedido</p>
        <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">Onde está o meu pedido?</h1>
        <p className="mt-3 text-[var(--muted-ink)]">Indica o número do pedido (está no email de confirmação ou na mensagem de WhatsApp) e o telefone que usaste.</p>
        <form onSubmit={search} className="mt-8 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <input required data-testid="track-number-input" className="field" placeholder="Nº do pedido (ex.: D1001)" value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} />
          <input required data-testid="track-phone-input" className="field" placeholder="Telefone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <button data-testid="track-submit-button" disabled={busy} className="btn-primary"><PackageSearch className="h-4 w-4" />{busy ? "A procurar…" : "Ver estado"}</button>
        </form>

        {order && (
          <div className="mt-10 rounded-[2rem] border border-[var(--line)] bg-white p-6 sm:p-8" data-testid="track-result">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-display text-3xl font-bold" data-testid="track-order-number">{order.number}</span>
              <span className="rounded-full bg-[var(--lilac)] px-3 py-1 text-xs font-semibold text-[var(--violet)]">{TYPE[order.type]}</span>
              <span className="text-xs text-[var(--muted-ink)]">{new Date(order.created_at).toLocaleDateString("pt-PT")}</span>
            </div>
            {order.type === "online" && order.payment_status !== "paid" && <p className="mt-4 text-sm text-amber-700" data-testid="track-payment-pending">Pagamento ainda não confirmado.</p>}
            <Timeline status={order.status} />
            {order.tracking && <p className="mt-6 text-sm" data-testid="track-tracking-code">Código de seguimento: <b>{order.tracking}</b></p>}
            <ul className="mt-6 space-y-3 border-t border-[var(--line)] pt-6 text-sm">
              {order.items.map((it) => {
                const p = products.find((x) => x.id === it.product_id);
                return (
                  <li key={`${it.product_id}-${optionsLabel(it.options)}`} className="flex flex-wrap items-center gap-3" data-testid={`track-item-${it.slug}`}>
                    <span className="flex-1">{it.qty}× {p ? <Link to={`/p/${p.slug}`} className="font-semibold hover:text-[var(--violet)]">{it.name}</Link> : it.name}{optionsLabel(it.options) && <span className="text-[var(--muted-ink)]"> · {optionsLabel(it.options)}</span>}</span>
                    {canReview && p && (it.reviewed
                      ? <span className="text-xs text-[var(--muted-ink)]" data-testid={`track-item-reviewed-${it.slug}`}>Avaliação enviada</span>
                      : <button data-testid={`track-review-button-${it.slug}`} onClick={() => setReviewing(p)} className="btn-ghost !px-4 !py-1.5 text-xs"><Star className="h-3.5 w-3.5" />Avaliar</button>)}
                  </li>
                );
              })}
              {order.request_items.map((it) => <li key={`${it.area}-${it.dimensions}`}>• {it.area} <span className="text-[var(--muted-ink)]">· Qtd: {it.quantity}{it.dimensions && ` · ${it.dimensions}`}</span></li>)}
            </ul>
            {order.total > 0 && <p className="mt-4 text-sm font-semibold">Total: {eur(order.total)}{order.zone && <span className="font-normal text-[var(--muted-ink)]"> · {ZONES[order.zone]?.split(" (")[0]}</span>}</p>}
          </div>
        )}
      </div>
      {reviewing && <ReviewDialog product={reviewing} preset={f} onClose={() => setReviewing(null)} onDone={search} />}
    </Container>
  );
}
